/**
 * Dev-only mock layer. Enabled with `VITE_MOCK_AUTH=true` (see .env.local).
 * Lets you run ONLY the dashboard (`pnpm dev:dashboard`) and click through every
 * page with realistic data — no backend, D1, OAuth, or email required.
 *
 * It is a no-op unless the flag is set, so production builds are unaffected.
 */
import { isUnlimited, PLAN_LIMITS } from "@currencycore/shared";
import type {
  ApiKeyEnvironment,
  ApiKeySummary,
  GrantKind,
  OrgPlan,
  OrgPlansResponse,
  OrgPlanView,
  PlanInterval,
  PlanRecord,
  PlanTier,
  UsageDay,
  UsageSeries,
  UsageStats,
} from "@currencycore/shared";

export const MOCK_ENABLED = import.meta.env.VITE_MOCK_AUTH === "true";

/** localStorage flag toggled by mock sign-in / sign-out. */
export const MOCK_SESSION_KEY = "cc_mock_session";

export const MOCK_USER = {
  id: "user_mock",
  name: "Ada Lovelace",
  email: "ada@currency-core.com",
  emailVerified: true,
  image: null as string | null,
  createdAt: new Date(),
  updatedAt: new Date(),
};

export interface MockOrg {
  id: string;
  name: string;
  slug: string;
  plan: PlanTier;
  /** The current mock user's role in this org. */
  role: MemberRole;
}

export const MOCK_ORGS: MockOrg[] = [
  { id: "org_acme", name: "Acme Corp", slug: "acme-corp", plan: "growth", role: "owner" },
  { id: "org_globex", name: "Globex", slug: "globex", plan: "free", role: "owner" },
  // An org the user only belongs to (someone else owns it) — lets you test "Leave".
  { id: "org_initech", name: "Initech", slug: "initech", plan: "growth_plus", role: "member" },
];

/** Create + persist a mock org so it appears in the switcher immediately. */
export function mockCreateOrg(name: string, slug: string): MockOrg {
  const org: MockOrg = {
    id: `org_${slug}_${Math.random().toString(36).slice(2, 6)}`,
    name,
    slug,
    plan: "free",
    role: "owner",
  };
  MOCK_ORGS.push(org);
  return org;
}

const PERIOD = new Date().toISOString().slice(0, 7); // YYYY-MM

function mkPlan(id: string, name: string, kind: PlanRecord["kind"], monthlyRequests: number | null, rateLimit: number | null, prices: PlanRecord["prices"], overageCents: number | null = null, overageUnit: number | null = null): PlanRecord {
  // Feature flags + AI/seat limits mirror PLAN_LIMITS for standard tiers; custom
  // plans are bespoke enterprise deals → all features, unlimited AI/seats.
  const def = id in PLAN_LIMITS ? PLAN_LIMITS[id as PlanTier] : null;
  return {
    id,
    name,
    kind,
    monthlyRequests,
    rateLimit,
    aiCreditLimit: def ? def.aiCreditLimit : null,
    seatLimit: def ? def.seatLimit : null,
    prices,
    overageCents,
    overageUnit,
    mcpEnabled: def ? def.mcpEnabled : true,
    ssoEnabled: def ? def.ssoEnabled : true,
    auditLogsEnabled: def ? def.auditLogsEnabled : true,
    auditRetentionDays: def ? def.auditRetentionDays : 1095,
    prioritySupportEnabled: def ? def.prioritySupportEnabled : true,
    customSlaEnabled: def ? def.customSlaEnabled : true,
    historyApiEnabled: def ? def.historyApiEnabled : true,
    dodoProductIds: {},
    archived: false,
    createdAt: null,
    updatedAt: null,
  };
}

const PLAN_CATALOG: PlanRecord[] = [
  mkPlan("free", "Free", "standard", 1_000, 60, { monthly: 0, annual: 0 }),
  mkPlan("starter", "Starter", "standard", 50_000, 120, { monthly: 900, annual: 9000 }, 50, 10000),
  mkPlan("growth", "Growth", "standard", 500_000, 600, { monthly: 2900, annual: 29000 }, 40, 10000),
  mkPlan("growth_plus", "Growth Plus", "standard", 2_000_000, 3000, { monthly: 7900, annual: 79000 }, 25, 10000),
  mkPlan("business", "Business", "standard", 10_000_000, 6000, { monthly: 19900, annual: 199000 }, 15, 10000),
  mkPlan("enterprise", "Enterprise", "standard", null, 12000, {}),
  mkPlan("plan_hooli", "Hooli Enterprise+", "custom", 5_000_000, 8000, { monthly: 49900, quarterly: 134900, annual: 499000 }, 20, 10000),
];
const planById = (id: string) => PLAN_CATALOG.find((p) => p.id === id) ?? PLAN_CATALOG[0]!;

interface Assign {
  planId: string;
  interval: PlanInterval | null;
  grantRequests?: number;
}
// One mock org is on a custom plan (with a grant) so you can see it in the dashboard.
const ASSIGN: Record<string, Assign> = {
  org_acme: { planId: "growth", interval: "monthly" },
  org_globex: { planId: "free", interval: null },
  org_initech: { planId: "plan_hooli", interval: "quarterly", grantRequests: 100_000 },
};
const USAGE_REQ: Record<string, number> = { free: 730, starter: 41_200, growth: 384_210, growth_plus: 1_511_234, business: 5_123_400, enterprise: 5_123_400, plan_hooli: 1_980_500 };

const assignOf = (orgId: string): Assign => ASSIGN[orgId] ?? { planId: "free", interval: null };

export function mockUsage(orgId: string): UsageStats {
  const a = assignOf(orgId);
  const plan = planById(a.planId);
  const base = isUnlimited(plan.monthlyRequests) ? null : plan.monthlyRequests;
  const grant = a.grantRequests ?? 0;
  const aiLimit = plan.aiCreditLimit;
  const aiUsed = mockAiRequests(orgId);
  const seatLimit = plan.seatLimit;
  return {
    organizationId: orgId,
    period: PERIOD,
    requests: USAGE_REQ[a.planId] ?? 730,
    limit: base,
    effectiveLimit: base === null ? null : base + grant,
    grantRequests: grant,
    plan: plan.kind === "custom" ? "custom" : (a.planId as OrgPlan),
    planLabel: plan.name,
    planKind: plan.kind,
    hardCapRequests: null,
    adminHardCapRequests: null,
    effectiveCap: null,
    aiCreditLimit: aiLimit,
    aiCreditsUsed: aiUsed,
    aiCreditsRemaining: aiLimit == null ? null : Math.max(0, aiLimit - aiUsed),
    purchasedAiCredits: a.planId === "growth" ? 8_500 : 0,
    seatLimit,
    seatsUsed: seatLimit == null ? 4 : Math.min(seatLimit, 2),
    currentPeriodStart: `${PERIOD}-01`,
    currentPeriodEnd: null,
    viewerRole: "owner",
  };
}

/** Deterministic daily series for mock mode (dev-only fake data). */
export function mockUsageDaily(orgId: string, days: number): UsageSeries {
  const total = USAGE_REQ[assignOf(orgId).planId] ?? 7_320;
  const perDay = total / 30;
  const today = new Date();
  const out: UsageDay[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - i);
    const jitter = 0.7 + 0.6 * (((i * 37 + 13) % 100) / 100);
    const requests = Math.round(perDay * jitter);
    out.push({
      date: d.toISOString().slice(0, 10),
      requests,
      mcpRequests: Math.round(requests * 0.18),
      aiRequests: Math.round(requests * 0.1),
    });
  }
  return { organizationId: orgId, days: out };
}

/** Monthly MCP subtotal for mock mode (≈18% of the org's requests). */
export function mockMcpRequests(orgId: string): number {
  return Math.round((USAGE_REQ[assignOf(orgId).planId] ?? 7_320) * 0.18);
}

/** Monthly AI subtotal for mock mode (≈10% of the org's requests). */
export function mockAiRequests(orgId: string): number {
  return Math.round((USAGE_REQ[assignOf(orgId).planId] ?? 7_320) * 0.1);
}

export function mockOrgPlans(orgId: string): OrgPlansResponse {
  const a = assignOf(orgId);
  const plan = planById(a.planId);
  const base = isUnlimited(plan.monthlyRequests) ? null : plan.monthlyRequests;
  const grant: OrgPlanView["grant"] = a.grantRequests
    ? { requests: a.grantRequests, kind: "until" as GrantKind, startPeriod: PERIOD, endPeriod: "2026-12", active: true }
    : null;
  const current: OrgPlanView = {
    planId: a.planId,
    label: plan.name,
    kind: plan.kind,
    interval: a.interval,
    monthlyLimit: base,
    effectiveLimit: base === null ? null : base + (grant?.requests ?? 0),
    grant,
    prices: plan.prices,
    overageCents: plan.overageCents,
    overageUnit: plan.overageUnit,
  };
  return {
    current,
    publicPlans: PLAN_CATALOG.filter((p) => p.kind === "standard"),
    customPlans: plan.kind === "custom" ? [plan] : [],
  };
}

// --- In-memory API key store (per org) so create/revoke are interactive ------
const keyStore: Record<string, ApiKeySummary[]> = {};

function seedKeys(orgId: string): ApiKeySummary[] {
  return [
    { id: "key_prod", name: "Production", maskedKey: "cc_live_••••a1b2", organizationId: orgId, environment: "live", createdAt: "2026-03-02T10:00:00.000Z", lastUsedAt: "2026-05-29T08:12:00.000Z", enabled: true },
    { id: "key_test", name: "Local testing", maskedKey: "cc_test_••••c3d4", organizationId: orgId, environment: "test", createdAt: "2026-04-15T14:30:00.000Z", lastUsedAt: null, enabled: true },
  ];
}

export function listMockKeys(orgId: string): ApiKeySummary[] {
  if (!keyStore[orgId]) keyStore[orgId] = seedKeys(orgId);
  return keyStore[orgId]!;
}

export function createMockKey(
  orgId: string,
  name: string,
  environment: ApiKeyEnvironment = "live",
): { key: string; summary: ApiKeySummary } {
  const rand = () => Math.random().toString(36).slice(2, 10);
  const prefix = environment === "test" ? "cc_test_" : "cc_live_";
  const full = `${prefix}${rand()}${rand()}`;
  const summary: ApiKeySummary = {
    id: `key_${rand()}`,
    name,
    maskedKey: `${prefix}••••${full.slice(-4)}`,
    organizationId: orgId,
    environment,
    createdAt: new Date().toISOString(),
    lastUsedAt: null,
    enabled: true,
    revealable: true,
  };
  listMockKeys(orgId).unshift(summary);
  return { key: full, summary };
}

export function revokeMockKey(orgId: string, keyId: string): void {
  keyStore[orgId] = listMockKeys(orgId).filter((k) => k.id !== keyId);
}

/**
 * Mock the Playground's sandbox demo key. Returns a fake `cc_demo_…` key with a
 * future `expiresAt` (~1h out) so the rotate-ahead logic stays a no-op in mock
 * mode. The key is hidden from the normal key list (not added to `keyStore`).
 */
export function mockDemoKey(orgId: string): { key: string; summary: ApiKeySummary; expiresAt: string } {
  const rand = () => Math.random().toString(36).slice(2, 10);
  const full = `cc_demo_${rand()}${rand()}`;
  const expiresAt = new Date(Date.now() + 60 * 60 * 1000).toISOString();
  const summary: ApiKeySummary = {
    id: `key_demo_${rand()}`,
    name: "Playground demo key",
    maskedKey: `cc_demo_••••${full.slice(-4)}`,
    organizationId: orgId,
    environment: "test",
    createdAt: new Date().toISOString(),
    lastUsedAt: null,
    enabled: true,
    revealable: false,
  };
  return { key: full, summary, expiresAt };
}

// --- Team: members + invitations (per org) ----------------------------------
export type MemberRole = "owner" | "admin" | "member";

export interface MockMember {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: MemberRole;
  createdAt: string;
}

export interface MockInvitation {
  id: string;
  email: string;
  role: MemberRole;
  status: string;
  expiresAt: string;
}

const rid = () => Math.random().toString(36).slice(2, 10);
const memberStore: Record<string, MockMember[]> = {};
const inviteStore: Record<string, MockInvitation[]> = {};

function seedMembers(orgId: string): MockMember[] {
  const myRole = MOCK_ORGS.find((o) => o.id === orgId)?.role ?? "owner";
  const ada: MockMember = { id: "mem_ada", userId: MOCK_USER.id, name: MOCK_USER.name, email: MOCK_USER.email, role: myRole, createdAt: "2026-02-01T09:00:00.000Z" };
  if (myRole === "owner") {
    return [
      ada,
      { id: "mem_grace", userId: "user_grace", name: "Grace Hopper", email: "grace@currency-core.com", role: "admin", createdAt: "2026-03-12T11:00:00.000Z" },
      { id: "mem_alan", userId: "user_alan", name: "Alan Turing", email: "alan@currency-core.com", role: "member", createdAt: "2026-04-20T16:30:00.000Z" },
    ];
  }
  // The user is a non-owner member; someone else owns this org.
  return [
    { id: "mem_sam", userId: "user_sam", name: "Sam Carter", email: "sam@currency-core.com", role: "owner", createdAt: "2026-01-10T09:00:00.000Z" },
    ada,
    { id: "mem_grace", userId: "user_grace", name: "Grace Hopper", email: "grace@currency-core.com", role: "admin", createdAt: "2026-03-12T11:00:00.000Z" },
  ];
}

export function mockListMembers(orgId: string): MockMember[] {
  if (!memberStore[orgId]) memberStore[orgId] = seedMembers(orgId);
  return memberStore[orgId]!;
}

export function mockListInvitations(orgId: string): MockInvitation[] {
  if (!inviteStore[orgId]) inviteStore[orgId] = [];
  return inviteStore[orgId]!;
}

export function mockInvite(orgId: string, email: string, role: MemberRole): void {
  // Honor the plan's seat cap (active members + pending invites), mirroring the
  // server's invite-member gate (auth.ts). null limit = unlimited.
  const seatLimit = planById(assignOf(orgId).planId).seatLimit;
  if (seatLimit != null) {
    const seatsUsed = mockListMembers(orgId).length + mockListInvitations(orgId).length;
    if (seatsUsed >= seatLimit) {
      throw new Error(
        `Your plan includes ${seatLimit} team seats and they're all in use (${seatsUsed}/${seatLimit}). Upgrade your plan to add more members.`,
      );
    }
  }
  const expiresAt = new Date(Date.now() + 7 * 864e5).toISOString();
  mockListInvitations(orgId).unshift({ id: `inv_${rid()}`, email, role, status: "pending", expiresAt });
}

export function mockUpdateRole(orgId: string, memberId: string, role: MemberRole): void {
  const m = mockListMembers(orgId).find((x) => x.id === memberId);
  if (m) m.role = role;
}

export function mockRemoveMember(orgId: string, memberId: string): void {
  memberStore[orgId] = mockListMembers(orgId).filter((x) => x.id !== memberId);
}

export function mockCancelInvite(orgId: string, invitationId: string): void {
  inviteStore[orgId] = mockListInvitations(orgId).filter((x) => x.id !== invitationId);
}

export const MOCK_CURRENT_USER_ID = MOCK_USER.id;

// --- Org create-gate / delete / leave / transfer / account (mock) -----------

/** Orgs the current user owns. */
function ownedOrgs(): MockOrg[] {
  return MOCK_ORGS.filter((o) => o.role === "owner");
}

/** Rule: the first org is always allowed; after that you must own >=1 PAID org. */
export function mockCanCreateOrg(): boolean {
  const owned = ownedOrgs();
  if (owned.length === 0) return true;
  return owned.some((o) => o.plan !== "free");
}

function removeOrgFromState(orgId: string): void {
  const i = MOCK_ORGS.findIndex((o) => o.id === orgId);
  if (i >= 0) MOCK_ORGS.splice(i, 1);
  delete keyStore[orgId];
  delete memberStore[orgId];
  delete inviteStore[orgId];
}

export function mockDeleteOrg(orgId: string): void { removeOrgFromState(orgId); }
export function mockLeaveOrg(orgId: string): void { removeOrgFromState(orgId); }

/** Promote target to owner, demote current owner to admin (single owner). */
export function mockTransferOwnership(orgId: string, newOwnerMemberId: string): void {
  const members = mockListMembers(orgId);
  members.forEach((m) => { if (m.role === "owner") m.role = "admin"; });
  const target = members.find((m) => m.id === newOwnerMemberId);
  if (target) target.role = "owner";
  const org = MOCK_ORGS.find((o) => o.id === orgId);
  if (org) org.role = target?.userId === MOCK_USER.id ? "owner" : "admin";
}

/** Owned orgs that still have other members — these block account deletion. */
export function mockOwnedOrgsBlockingDeletion(): { id: string; name: string; members: number }[] {
  return ownedOrgs()
    .map((o) => ({ id: o.id, name: o.name, members: mockListMembers(o.id).length }))
    .filter((o) => o.members > 1);
}

/** On account deletion: delete sole-member owned orgs + leave the rest. */
export function mockPurgeUserOrgs(): void {
  for (const o of [...MOCK_ORGS]) removeOrgFromState(o.id);
}

export function mockUpdateUserName(name: string): void {
  MOCK_USER.name = name;
}
